import { type AxiosRequestConfig } from 'axios'

import { AbstractSdk, type AbstractSdkConstructorParameters, type ControllerApiConstructor } from '../abstract'
import { VisitorType } from '../enums'
import {
  type MetadataContent,
  type TrackedEvent,
  type TrackedEventEventTypeEnum,
  type TrackedEventRequest,
  type TrackedFrontError,
  TrackingControllerApi,
  type TrackingRequest,
  type TrackingResponseSuccess,
  type TrackingUpdateConsentRequest,
} from '../generated'
import {
  CONSENT_ID_STORAGE,
  CONSENT_VENDORS_ALL_STORAGE,
  CONSENT_VENDORS_FILTER_STORAGE,
  EVENT_TO_CONSENT_STORAGE,
  EVENTS_CLICK_STORAGE,
  EVENTS_PUSH_STORAGE,
  FIRST_VISIT_STORAGE,
  NAVIGATION_PREVIOUS_STORAGE,
  SEARCH_USAGE_STORAGE,
  type StorageSdk,
  VISITOR_TYPE_STORAGE,
} from '../storage'
import { EVENTS_CLICK_HEADER, EVENTS_PUSH_HEADER } from '../userConstants'
import { removeEmptyStringsFromObject } from '../utils/cleanObject'

import { FirstVisitData, FirstVisitDataMapper, type VisitorTypeData, VisitorTypeDataMapper } from './expirableData'

type PreviousData = {
  previousPageName?: string
  clickPosition?: string
}

export enum PushEvent {
  PushTableauDesGares = 'PushTableauDesGares',
  PushItinerairesEnregistres = 'PushItinerairesEnregistres',
  PushRetourExpress = 'PushRetourExpress',
  PushFinalisationOptionSansEco = 'PushFinalisationOptionSansEco',
  PushFinalisationOptionAvecEco = 'PushFinalisationOptionAvecEco',
  PushEchangeProactif = 'PushEchangeProactif',
  CompositionTrainDoorToDoor = 'CompositionTrainDoorToDoor',
  CompositionTrainTableauDeGare = 'CompositionTrainTableauDeGare',
  CompositionConsultationCommande = 'CompositionConsultationCommande',
  PushAlertePetitPrix = 'PushAlertePetitPrix',
  PushAlerteMax = 'PushAlerteMax',
  PushAlertePetitPrixValide = 'PushAlertePetitPrixValide',
  PushAlerteMaxValide = 'PushAlerteMaxValide',
  PushAlerteMaxAutoValide = 'PushAlerteMaxAutoValide',
}

export enum PushType {
  INFORMATION = 'IV_Information',
  POSITIVE = 'IV_Positif',
  WARNING = 'IV_Warning',
  ERROR = 'IV_Erreur',
}
export enum ExcludeTrackingFrontPageName {
  AjoutProduit = 'AjoutProduit',
}

export enum PageName {
  AccueilJustificatifs = 'AccueilJustificatifs',
  CancellationQuotationCard = 'CancellationQuotationCard',
  CancellationQuotationPass = 'CancellationQuotationPass',
  CatalogueHomepage = 'Catalogue',
  ConditionsTarifaires = 'ConditionsTarifaires',
  ConfirmationCancellationFinalization = 'confirmationCancellationFinalization',
  ConfirmationCardCancellationFinalization = 'confirmationCardCancellationFinalization',
  ConfirmationCommande = 'ConfirmationCommande',
  ConsultationTripDetail = 'consultationTripDetail',
  ConsultationTripSearchResults = 'consultationTripSearchResults',
  Coordonnees = 'Coordonnees',
  FicheTrajet = 'FicheTrajet',
  Homepage = 'Homepage',
  ModeDeRetrait = 'ModeDeRetrait',
  Panier = 'Panier',
  Payment = 'Payment',
  PaymentValidation = 'paymentValidation',
  ResultatsItineraires = 'ResultatsItineraires',
  StationTimetableResults = 'TableauHoraires',
  StationTimetableSearch = 'Horaires',

  // Exchange
  EchangeSelectionTrajetPassager = 'EchangeSelectionTrajetPassager',
  EchangeSelectionTrajet = 'EchangeSelectionTrajet',
  EchangeSelectionPassager = 'EchangeSelectionPassager',
  EchangeMoteur = 'EchangeMoteur',
  EchangeDevisAller = 'EchangeDevisAller',
  EchangeDevisRetour = 'EchangeDevisRetour',
  EchangePanier = 'EchangePanier',
  EchangePaiement = 'EchangePaiement',
  ConfirmationEchange = 'ConfirmationEchange',

  // annulation
  AnnulationSelectionTrajet = 'AnnulationSelectionTrajet',
  AnnulationSelectionPassager = 'AnnulationSelectionPassager',
  AnnulationPanier = 'AnnulationPanier',
  AnnulationSelectionTrajetPassager = 'AnnulationSelectionTrajetPassager',

  // CCL*
  BikeShelters = 'AbrisVelo',
  BikeSheltersForm = 'AbrisVeloActivation',
  CCLAccueil = 'CCLAccueil',
  CCLCompagnons = 'CCLCompagnons',
  CCLMesInformations = 'CCLMesInformations',
  Tickets = 'MesTitres',
  MesVoyagesPasses = 'MesVoyagesPasses',
  VoyageDeplieAller = 'VoyageDeplieAller',
  VoyageDeplieRetour = 'VoyageDeplieRetour',
  RechercheTitre = 'RechercheTitre',
  CCLMoyensPaiement = 'CCLMoyensPaiement',
  CCLCompagnonAjouter = 'CCLCompagnonAjouter',
  CCLCompagnonModifier = 'CCLCompagnonModifier',
  CarteAbonnementPass = 'CarteAbonnementPass',
  CCLAnimalAjouter = 'CCLAnimalAjouter',
  CCLGestionDeConsentement = 'CCLGestionDeConsentement',
  CCLTheme = 'CCLTheme',
  CCLAbrisVelo = 'CCLAbrisVelo',
  InfoTrafic = 'InfoTrafic',
  CRCForm = 'FormulaireContact',
  CRCFormStep2 = 'FormulaireDetailDemande',
  TicketDetails = 'DetailTitres',
  TicketsHistory = 'HistoriqueTitres',
  TicketQRCode = 'TicketingQRcode',
  CCLPlaces = 'AdressesFavoris',
  CCLPlacesAddHome = 'AjoutAdresseMaison',
  CCLPlacesAddOther = 'AjoutAdresseAutre',
  CCLPlacesAddWork = 'AjoutAdresseTravail',
  CCLPlacesUpdatePlace = 'ModificationAdresse',
  CartePersonnelleZou = 'CartePersonnelleZou',
  CCLInvoiceRequest = 'CCLDemandeDeFacture',
}

export enum ClickEventId {
  PropositionBBC = 'PropositionBBC',
  ClicDetailTrajet = 'ClicDetailTrajet',
  AjoutCalendrier = 'AjoutCalendrier',
  PlanReserverVotreTransporteur = 'PlanReserverVotreTransporteur',
  RechercheLibre = 'RechercheLibre',
  RechercheAutoCompletion = 'RechercheAutoCompletion',
  ClicUpsellPremiere = 'ClicUpsellPremiere',
  EchangeOuigoContinuer = 'EchangeOuigoContinuer',
  TelechargementJustificatif = 'TelechargementJustificatif',
  AlertePetitPrixSuppression = 'AlertePetitPrixSuppression',
  AlerteMaxSuppression = 'AlerteMaxSuppression',
  MasquerRenouvellementProactifCCL = 'MasquerRenouvellementProactifCCL',
  TrajetRecoML = 'TrajetRecoML',
  TelechargementJustificatifAchat = 'TelechargementJustificatifAchat',
  EnregistrementAdresse = 'EnregistrementAdresse',
  ConsultationAdresse = 'ConsultationAdresse',
  SuppressionAdresse = 'SuppressionAdresse',
  AjoutAdresse = 'AjoutAdresse',
  AbrisVeloConfirmationActivation = 'AbrisVeloConfirmationActivation',
  CRCEnvoiFormulaire = 'EnvoyerFormulaireContact',
  PlacesDeleteHome = 'SuppressionAdresseMaison',
  PlacesDeleteWork = 'SuppressionAdresseTravail',
  PlacesDeleteOther = 'SuppressionAdresseAutre',
  ReservationPlaceCar = 'ReservationCar',
  SuppressionCompte = 'SuppressionCompte',
  ConfirmationSuppressionCompte = 'ConfirmationSuppressionCompte',
  ValidationAdresseMaison = 'ValidationAdresseMaison',
  ValidationAdresseTravail = 'ValidationAdresseTravail',
  ValidationAdresseAutre = 'ValidationAdresseAutre',
  AbonnementAnnuel = 'AbonnementAnnuel',
  AbonnementScolaire = 'AbonnementScolaire',
  EnregistrerCartePersonnelle = 'EnregistrerCartePersonnelle',
  AffichageTitrePdf = 'AffichageTitrePdf',
  AppStoreTelechargement = 'AppStoreTelechargement',
  GooglePlayTelechargement = 'GooglePlayTelechargement',
}

// Theme used by the user.
// Native = Theme by default unchanged,
// Custom = Theme changed by the user from the app
// System = Theme changed by the user from the OS System
export enum ColorTheme {
  DarkMode_Native = 'DarkMode_Native',
  DarkMode_Custom = 'DarkMode_Custom',
  LightMode_Custom = 'LightMode_Custom',
  DarkMode_System = 'DarkMode_System',
  LightMode_System = 'LightMode_System',
}

export enum SearchUsage {
  LIBRE = 'LIBRE',
  AUTOCOMPLETION = 'AUTOCOMPLETION',
  SUGGESTION = 'SUGGESTION',
  HISTORIQUE = 'HISTORIQUE',
}

export class TrackingSdk extends AbstractSdk<TrackingControllerApi> {
  readonly storageSdk: StorageSdk

  constructor(...[configuration, ...rest]: AbstractSdkConstructorParameters) {
    super(configuration, ...rest)
    this.storageSdk = this.userSdk.storageSdk
  }

  protected getControllerApiConstructor(): ControllerApiConstructor<TrackingControllerApi> {
    return TrackingControllerApi
  }

  init(): void {
    this.initVisitorType()
    this.initPreviousPageDatas()
    this.initDeferredEvents()
  }

  initFirstVisit(): void {
    const rawFirstVisitDatas = this.storageSdk.localStorage()?.getItem(FIRST_VISIT_STORAGE)

    if (!rawFirstVisitDatas) {
      const newValue = new FirstVisitData(new Date()).resetExpirationDate()
      this.storageSdk.localStorage()?.setItem(FIRST_VISIT_STORAGE, FirstVisitDataMapper.toJSON(newValue))
    }
  }

  initVisitorType(): void {
    this.initFirstVisit()
    const firstVisitData: FirstVisitData = FirstVisitDataMapper.fromJSON(
      this.storageSdk.localStorage()?.getItem(FIRST_VISIT_STORAGE),
    )
    const visitorTypeData: VisitorTypeData = VisitorTypeDataMapper.fromJSON(
      this.storageSdk.localStorage()?.getItem(VISITOR_TYPE_STORAGE),
    )

    if (
      (visitorTypeData.visitorType === VisitorType.NEW_USER && firstVisitData.isExpired()) ||
      visitorTypeData.isExpired()
    ) {
      this.updateVisitorType(visitorTypeData, firstVisitData)
    } else {
      // rewrite object in case of corruption
      this.storageSdk.localStorage()?.setItem(FIRST_VISIT_STORAGE, FirstVisitDataMapper.toJSON(firstVisitData))
      this.storageSdk.localStorage()?.setItem(VISITOR_TYPE_STORAGE, VisitorTypeDataMapper.toJSON(visitorTypeData))
    }
  }

  private updateVisitorType(visitorTypeData: VisitorTypeData, firstVisitData: FirstVisitData) {
    if (visitorTypeData.isExpired()) {
      visitorTypeData.setVisitorType(VisitorType.NEW_USER).resetExpirationDate()
      firstVisitData.resetExpirationDate()
    } else if (firstVisitData.isExpired()) {
      visitorTypeData.setVisitorType(VisitorType.KNOWN_USER)
    } else {
      visitorTypeData.setVisitorType(VisitorType.NEW_USER)
    }

    this.storageSdk.localStorage()?.setItem(FIRST_VISIT_STORAGE, FirstVisitDataMapper.toJSON(firstVisitData))
    this.storageSdk.localStorage()?.setItem(VISITOR_TYPE_STORAGE, VisitorTypeDataMapper.toJSON(visitorTypeData))
  }

  initPreviousPageDatas(): void {
    if (this.storageSdk.sessionStorage()?.getItem(NAVIGATION_PREVIOUS_STORAGE) === null) {
      const initData: PreviousData = {
        previousPageName: '',
        clickPosition: '',
      }
      this.storageSdk.sessionStorage()?.setItem(NAVIGATION_PREVIOUS_STORAGE, JSON.stringify(initData))
    }
  }

  private initDeferredEvents(): void {
    if (this.storageSdk.sessionStorage()?.getItem(EVENTS_PUSH_STORAGE) == null) {
      this.updateDeferredEventsPush(null)
    }

    if (this.storageSdk.sessionStorage()?.getItem(EVENTS_CLICK_STORAGE) == null) {
      this.updateDeferredEventsClick(null)
    }
  }

  private async getS2sVendors(): Promise<string[]> {
    try {
      const response = await this.api.getS2sVendors(this.userSdk.getBffHeader(), this.userSdk.createAxiosOptions(false))
      const { vendors } = response.data
      this.storageSdk.localStorage()?.setItem(CONSENT_VENDORS_FILTER_STORAGE, vendors.join())

      return vendors
    } catch {
      return this.storageSdk.localStorage()?.getItem(CONSENT_VENDORS_FILTER_STORAGE)?.split(',') ?? new Array('')
    }
  }

  private isConsentInitialized(): boolean {
    return this.storageSdk.localStorage()?.getItem(CONSENT_ID_STORAGE) != null
  }

  private isExemptionActive(userVendors: unknown[] | string): boolean {
    return !userVendors.includes('c:omniture-adobe-analytics')
  }

  private initializeConsentIds(isExemptionActive: boolean): [string, string] {
    const visitorId = this.userSdk.getOrInitVisitorId() ?? this.userSdk.createVisitorId()
    const newId = this.userSdk.createVisitorId()

    return isExemptionActive ? [newId, visitorId] : [visitorId, newId]
  }

  private updateExemptId(isExemptionActive: boolean): string | undefined {
    const wasExemptionActive = this.isExemptionActive(this.userSdk.getConsentVendors())

    return !wasExemptionActive && isExemptionActive ? this.userSdk.createVisitorId() : undefined
  }

  private async filterVendors(userVendors: unknown[]): Promise<string> {
    if (userVendors.length < 1) {
      return ''
    }
    const s2sVendors = await this.getS2sVendors()
    const filteredVendors = Array.from(s2sVendors).filter((vendor: string) => {
      const vendorNumber = parseInt(vendor, 10)

      return userVendors.includes(Number.isNaN(vendorNumber) ? vendor : vendorNumber)
    })

    return filteredVendors.join()
  }

  async setSearchUsage(usage: SearchUsage): Promise<void> {
    this.storageSdk.sessionStorage()?.setItem(SEARCH_USAGE_STORAGE, usage)
  }

  async updateConsentData(userVendors: unknown[], consentString: string): Promise<void> {
    const consentVendors = userVendors.join()
    const previousVendors = this.storageSdk.localStorage()?.getItem(CONSENT_VENDORS_ALL_STORAGE)

    if (consentVendors !== previousVendors) {
      // Process consent IDs
      let consentId
      let exemptId
      const hasExemption = this.isExemptionActive(userVendors)

      if (this.isConsentInitialized()) {
        exemptId = this.updateExemptId(hasExemption)
      } else {
        ;[consentId, exemptId] = this.initializeConsentIds(hasExemption)
      }

      // Process vendors
      const filteredVendors = await this.filterVendors(userVendors)

      // Update storage (all vendors for web tags + filtered vendors for S2S)
      this.storageSdk.localStorage()?.setItem(CONSENT_VENDORS_ALL_STORAGE, consentVendors)
      this.userSdk.updateUserConsentData(filteredVendors, consentString, consentId, exemptId)

      // Update pending-consent events if any
      const eventToConsent = this.getDeferredEventToConsent()

      if (eventToConsent) {
        const response = await this.updateWithConsent(eventToConsent)

        if (response.ok) {
          this.replaceDeferredEventToConsent(null)
        }
      }
    }
  }

  async updateMissingConsentData(userVendors: unknown[]): Promise<void> {
    if (this.isConsentInitialized() && this.storageSdk.localStorage()?.getItem(CONSENT_VENDORS_ALL_STORAGE) == null) {
      // Add all vendors to storage (for web tags)
      this.storageSdk.localStorage()?.setItem(CONSENT_VENDORS_ALL_STORAGE, userVendors.join())
    }
  }

  /**
   * Iterate recursively through DOM to find an element to identify click position via data-attributes
   */
  findClickPosition(el: HTMLElement): string {
    let link: string | null = null
    let block: string | null = null
    let element: HTMLElement | null = el

    do {
      if (link === null && element.dataset.rfrrlink !== undefined) {
        link = element.dataset.rfrrlink
      }

      if (block === null && element.dataset.rfrrblock !== undefined) {
        block = element.dataset.rfrrblock
      }

      element = element.parentElement
    } while (element && (link == null || block == null))

    const rfrrs = [link, block].filter((item): item is string => !!item)

    if (rfrrs.length === 0) {
      return ''
    }

    return rfrrs.join(':')
  }

  updateClickPosition(evt: MouseEvent): void {
    const newClickPosition = this.findClickPosition(evt.target as HTMLElement)
    const previousDatas = this.storageSdk.sessionStorage()?.getItem(NAVIGATION_PREVIOUS_STORAGE) || ''
    const { previousPageName } = JSON.parse(previousDatas)
    const objData: PreviousData = {
      previousPageName,
      clickPosition: newClickPosition,
    }
    this.storageSdk.sessionStorage()?.setItem(NAVIGATION_PREVIOUS_STORAGE, JSON.stringify(objData))
  }

  updatePreviousPageName(pageName?: string): void {
    const previousData: PreviousData = {
      previousPageName: pageName,
      clickPosition: '',
    }
    this.storageSdk.sessionStorage()?.setItem(NAVIGATION_PREVIOUS_STORAGE, JSON.stringify(previousData))
  }

  updateEmails(emailHidden?: string, emailStrong?: string, emailStronger?: string): void {
    if (emailHidden?.trim() && this.userSdk.getEmailHidden() !== emailHidden) {
      this.userSdk.setEmailHidden(emailHidden?.trim())
    }

    if (emailStrong?.trim() && this.userSdk.getEmailStrong() !== emailStrong) {
      this.userSdk.setEmailStrong(emailStrong?.trim())
    }

    if (emailStronger?.trim() && this.userSdk.getEmailStronger() !== emailStronger) {
      this.userSdk.setEmailStronger(emailStronger?.trim())
    }
  }

  updateDatalayer(datalayer?: MetadataContent, forcePreviousPage = false): void {
    try {
      if (forcePreviousPage) {
        this.updatePreviousPageName(datalayer?.stringValues?.page_name)
      }
      const deferredEventToConsent = datalayer?.json

      if (deferredEventToConsent) {
        this.replaceDeferredEventToConsent(deferredEventToConsent)
      }
      this.updateEmails(
        datalayer?.stringValues?.user_emails_hidden,
        datalayer?.stringValues?.user_emails_strong,
        datalayer?.stringValues?.user_emails_stronger,
      )
    } catch {} // eslint-disable-line no-empty
  }

  private updateDeferredEventsPush = (events: Set<string> | null): void => {
    this.storageSdk.sessionStorage()?.setItem(EVENTS_PUSH_STORAGE, JSON.stringify(Array.from(events || [])))
  }

  private updateDeferredEventsClick = (events: Set<string> | null): void => {
    this.storageSdk.sessionStorage()?.setItem(EVENTS_CLICK_STORAGE, JSON.stringify(Array.from(events || [])))
  }

  private async trackEventRequest(trackedEventRequest: TrackedEventRequest): Promise<TrackingResponseSuccess> {
    try {
      const response = await this.api.trackEvents(
        this.userSdk.getBffHeader(),
        trackedEventRequest,
        this.userSdk.createAxiosOptions(false),
      )

      return response.data
    } catch {
      return { ok: false }
    }
  }

  async trackEvent(
    eventName: string,
    eventType: TrackedEventEventTypeEnum,
    pageName = '',
    timestamp = Date.now(),
    allowDeferred = true,
    eventParams?: Record<string, string>,
    isInternal = false,
  ): Promise<TrackingResponseSuccess> {
    try {
      if (eventType === 'PUSH' && allowDeferred) {
        this.deferEventPush(eventName)

        return { ok: true }
      }

      if (eventType === 'CLICK' && allowDeferred) {
        this.deferEventClick(eventName)

        return { ok: true }
      }
      const trackedEvent: TrackedEvent = {
        event: eventName,
        eventType,
        pageName,
        timestamp,
        eventParams,
        isInternal,
      }

      return await this.trackEvents(trackedEvent)
    } catch {
      return { ok: false }
    }
  }

  private deferEventPush(pushName: string): void {
    const events = this.userSdk.getDeferredEventsPush()
    events.add(pushName)
    this.updateDeferredEventsPush(events)
  }

  private deferEventClick(clickName: string): void {
    const events = this.userSdk.getDeferredEventsClick()
    events.add(clickName)
    this.updateDeferredEventsClick(events)
  }

  private getDeferredEventToConsent(): string | null {
    return this.storageSdk.sessionStorage()?.getItem(EVENT_TO_CONSENT_STORAGE) ?? null
  }

  private replaceDeferredEventToConsent = (event: string | null): void => {
    if (event) {
      this.storageSdk.sessionStorage()?.setItem(EVENT_TO_CONSENT_STORAGE, event)
    } else {
      this.storageSdk.sessionStorage()?.removeItem(EVENT_TO_CONSENT_STORAGE)
    }
  }

  private async trackEvents(...trackedEvents: TrackedEvent[]): Promise<TrackingResponseSuccess> {
    const trackedEventRequest: TrackedEventRequest = {
      events: trackedEvents,
    }

    return this.trackEventRequest(trackedEventRequest)
  }

  private async genericTrackPage(trackingRequest: TrackingRequest): Promise<TrackingResponseSuccess> {
    try {
      const axiosOptions = this.userSdk.createAxiosOptions(false, { headers: this.getDeferredEventsHeadersOptions() })
      const responseData = (
        await this.api.trackPage(trackingRequest.pageName, this.userSdk.getBffHeader(), trackingRequest, axiosOptions)
      ).data
      this.updateDatalayer(responseData.datalayer)

      return responseData
    } catch {
      return { ok: false }
    }
  }

  async trackPage(pageName: string, mobilityProductsTransportType?: string): Promise<TrackingResponseSuccess> {
    return this.genericTrackPage({ pageName, mobilityProductsTransportType })
  }

  async trackExternalPage({
    pageName,
    pageSeoType,
    pageSectionType,
  }: TrackingRequest): Promise<TrackingResponseSuccess> {
    return this.genericTrackPage({
      pageName,
      pageSeoType,
      pageSectionType,
    })
  }

  async trackClick(clickId: string | undefined, pageName: string, isDeferred = true): Promise<TrackingResponseSuccess> {
    if (clickId === undefined) {
      return { ok: false }
    }
    let eventName

    try {
      const url = new URL(clickId)
      eventName = url.origin + url.pathname
    } catch {
      eventName = clickId
    }

    if (isDeferred) {
      this.deferEventClick(eventName)

      return { ok: true }
    }

    try {
      const trackedEventRequest: TrackedEventRequest = {
        events: [
          {
            pageName,
            event: eventName,
            eventType: 'CLICK',
            timestamp: Date.now(),
            isInternal: false,
          },
        ],
      }
      const axiosOptions = this.userSdk.createAxiosOptions(false)
      const response = await this.api.trackEvents(this.userSdk.getBffHeader(), trackedEventRequest, axiosOptions)

      return response.data
    } catch {
      return { ok: false }
    }
  }

  async trackError(
    type: string,
    source: string,
    message: string,
    params?: Record<string, string>,
    subtype?: string,
  ): Promise<TrackingResponseSuccess> {
    try {
      const error: TrackedFrontError = {
        type,
        source,
        message,
        params: params ? removeEmptyStringsFromObject(params) : undefined,
        subtype,
        timestamp: Date.now(),
        isInternal: false,
      }

      const axiosOptions = this.userSdk.createAxiosOptions(false)
      const response = await this.api.trackError(type ?? 'UNTYPED', this.userSdk.getBffHeader(), error, axiosOptions)

      return response.data
    } catch {
      return { ok: false }
    }
  }

  async updateWithConsent(event: string): Promise<TrackingResponseSuccess> {
    try {
      const updateRequest: TrackingUpdateConsentRequest = { event }
      const axiosOptions = this.userSdk.createAxiosOptions(false)
      const responseData = (await this.api.updateWithConsent(this.userSdk.getBffHeader(), updateRequest, axiosOptions))
        .data
      this.updateDatalayer(responseData.datalayer)

      return responseData
    } catch {
      return { ok: false }
    }
  }

  private getDeferredEventsHeadersOptions(): AxiosRequestConfig['headers'] {
    const eventsPush = this.userSdk.getDeferredEventsPush()
    const headers: AxiosRequestConfig['headers'] = {}

    if (eventsPush.size > 0) {
      headers[EVENTS_PUSH_HEADER] = Array.from(eventsPush).join(',')
      this.updateDeferredEventsPush(null)
    }

    const eventsClick = this.userSdk.getDeferredEventsClick()

    if (eventsClick.size > 0) {
      headers[EVENTS_CLICK_HEADER] = Array.from(eventsClick).join(',')
      this.updateDeferredEventsClick(null)
    }

    return headers
  }
}

export const getMarketLanguage = (): string => {
  const htmlLangAttribute = document.documentElement.lang as string

  if (htmlLangAttribute) {
    const htmlLang = htmlLangAttribute.split(/[_-]+/)

    if (!htmlLang[1]) {
      return `${htmlLang[0]}_${htmlLang[0].toUpperCase()}`
    }

    return `${htmlLang[0]}_${htmlLang[1].toUpperCase()}`
  }

  return 'fr_FR'
}

export const getTrackingOs = (osName?: string, osVersion?: string): string | 'unknown' => {
  if (!osName) {
    return 'unknown'
  }

  return osVersion ? `${osName} (${osVersion})` : osName
}

export const getTrackingDeviceType = (deviceType?: string): string => {
  switch (deviceType) {
    case undefined:
    case 'browser':
      return 'desktop'
    case 'mobile':
      return 'responsive'
    default:
      return deviceType
  }
}
